import React, {FormEvent, useEffect, useState} from "react";
import { IAttribute, IMetadataAttributeOptions } from "../../models/Search";
import {
    FocusZone,
    FocusZoneDirection,
    getFocusStyle,
    getTheme,
    Icon,
    ITheme,
    List,
    mergeStyleSets
} from "@fluentui/react";
import {Dropdown, IDropdownOption} from "@fluentui/react/lib/Dropdown";

interface IFacetSelectionProps {
    facet: string;
    facets: IMetadataAttributeOptions;
    attributeType: string;
    hidden?: boolean;
    mustFilter?: IAttribute[];
    boostFilter?: IAttribute[];
    excludeFilter?: IAttribute[];
    onAddMust: (attribute: IAttribute) => void;
    onAddBoost: (attribute: IAttribute) => void;
    onAddExclude: (attribute: IAttribute) => void;
    onRemoveCriteria: (attribute: IAttribute) => void;
}

export const FacetSelection: React.FC<IFacetSelectionProps> = (props) => {
    
    const [mustFilter, setMustFilter] = useState<IAttribute[]>();
    const [boostFilter, setBoostFilter] = useState<IAttribute[]>();
    const [excludeFilter, setExcludeFilter] = useState<IAttribute[]>();
    
    const options : IDropdownOption[] = [
        {
            key: "MUST",
            text: "Must Have",
            data: {icon: 'HeartFill', iconColor: 'red'},
        },
        {
            key: "BOOST",
            text: "Nice to Have",
            data: {icon: 'Like', iconColor: 'blue'},
        },
        {
            key: "EXCLUDE",
            text: "Must Not Have",
            data: {icon: 'StatusCircleBlock', iconColor: 'red'},
        },
        {
            key: "NONE",
            text: "Clear Selection",
            data: {icon: 'CircleRing'},
        },
    ];
    useEffect(() => {
        setMustFilter(props.mustFilter);
        setBoostFilter(props.boostFilter);
        setExcludeFilter(props.excludeFilter);
    }, [props.boostFilter, props.excludeFilter, props.mustFilter]);
    
    
    const getFilterValue = (value : string, name : string) => {
        const tryMust = mustFilter?.filter(a => a.name == name && a.value == value);
        if(tryMust && tryMust.length == 1) {
            return "MUST"
        }
        const tryExcludes = excludeFilter?.filter(a => a.name == name && a.value == value);
        if(tryExcludes && tryExcludes.length == 1) {
            return "EXCLUDE"
        }
        const tryBoost = boostFilter?.filter(a => a.name == name && a.value == value);
        if(tryBoost && tryBoost.length == 1) {
            return "BOOST"
        }
        
        return "NONE";
    }
    
    function handleChange(event: FormEvent<HTMLDivElement>, option?: IDropdownOption, value?: string) {
        if(option && value) {
            const facet: IAttribute = {
                type: props.attributeType,
                name: props.facet,
                value: value,
            };
            if (facet?.name) {
                switch (option.key) {
                    case "MUST":
                        props.onAddMust(facet);
                        break;
                    case "BOOST":
                        props.onAddBoost(facet);
                        break;
                    case "EXCLUDE":
                        props.onAddExclude(facet);
                        break;
                    default:
                        props.onRemoveCriteria(facet);
                        break;
                }
            }
        }
    }
    const theme: ITheme = getTheme();
    const { palette, semanticColors, fonts } = theme;
    // The control renders as a single bare icon (custom title, suppressed caret), so it only needs
    // icon width. Fluent's dropdown is otherwise ~189px intrinsically, which in a 282px row left the
    // value about 64px and nothing for its flex-grow to claim — hence the truncated values.
    const CONTROL_WIDTH = 34;
    const classNames = mergeStyleSets({
        itemCell: [
            getFocusStyle(theme, { inset: -1 }),
            {
                minHeight: 30,
                padding: 10,
                boxSizing: 'border-box',
                borderBottom: `1px solid ${semanticColors.bodyDivider}`,
                display: 'flex',
                // Centered so the value sits on the same line as its selection control, including
                // when a long value wraps to a second line.
                alignItems: 'center',
                selectors: {
                    '&:hover': { background: palette.neutralLight },
                },
            },
        ],
        itemImage: {
            flexShrink: 0,
        },
        // Fixed-width host for the selection control. App.css sets `.ms-Dropdown-container
        // { width: 100% }` globally, which beats anything merge-styles puts on the Dropdown's own
        // root (same specificity, but App.css is injected later). Sizing a wrapper instead makes
        // that 100% resolve against 100% of CONTROL_WIDTH — no specificity fight, and nothing here
        // depends on the global rule staying as it is.
        control: {
            width: CONTROL_WIDTH,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
        },
        itemContent: {
            marginLeft: 10,
            overflow: 'hidden',
            flexGrow: 1,
            // Flex items default to min-width:auto, which floors them at their content width.
            // Without this the value can still push the row wider than the panel.
            minWidth: 0,
        },
        itemName: [
            fonts.xLarge,
            {
                // Values are routinely longer than the criteria panel is wide, so wrap rather than
                // clip. Reclaiming the width the selection control was holding (see CONTROL_WIDTH)
                // means most now fit on one line anyway.
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
            },
        ],
        itemIndex: {
            fontSize: fonts.small.fontSize,
            backgroundColor: 'blue',
            marginBottom: 10,
        },
        chevron: {
            alignSelf: 'center',
            marginLeft: 10,
            color: palette.neutralTertiary,
            fontSize: fonts.large.fontSize,
            flexShrink: 0,
        },
    });
    const items = Object.entries(props.facets).map((facetValue) => {
        const facet: IAttribute = {
            type: props.attributeType,
            name: props.facet,
            value: facetValue[0],
        };
        return facet;
    });
    const iconStyles = (color? : string) => { 
        return {
            marginRight: '8px',
            color: color ?? 'black'}
        };
    const onRenderOption = (option?: IDropdownOption): JSX.Element => {
        return (
            <div style={{width:'120px'}}>
                {option?.data && option?.data.icon && (
                    <div style={{display:'inline-flex'}}><Icon style={iconStyles(option.data.iconColor)} iconName={option.data.icon} aria-hidden="true" title={option.data.icon} />{option.text}</div>
                )}
            </div>
        );
    };

    const contentStyles = mergeStyleSets({
        title: {
            borderWidth: 'unset',
            borderStyle: 'unset',
            borderColor: 'unset',
            borderRadius: 'unset',
            backgroundColor: 'unset',
            // Fluent's title padding is `0 28px 0 8px`, which would push the icon out of a box
            // this narrow. Zeroed and centered instead.
            padding: 0,
            width: 'auto',
            textAlign: 'center',
        },
        // The caret renders as an empty fragment, but its wrapper still reserves space.
        caretDownWrapper: {
            display: 'none',
        },
        // Fluent derives the selected-item background from neutralLight, which in this theme is a
        // green-tinted #cfdcb5 — a shade off the #e2e9cb attribute header band, so a selected option
        // read as a section header. A true neutral separates them, and stays legible behind the
        // red/blue option icons in a way a saturated brand green would not.
        dropdownItemSelected: {
            backgroundColor: palette.neutralQuaternary,
            color: palette.neutralDark,
            fontWeight: 600,
            selectors: {
                '&:hover': { backgroundColor: palette.neutralTertiaryAlt },
                '&:focus': { backgroundColor: palette.neutralQuaternary },
            },
        },
    });
    
    const optionElement = (option: string) => {
        let trigger = <span className='ac-attribute-dropdown-item'><Icon iconName={"CircleRing"}/></span>;
        switch (option) {
            case 'BOOST':
                trigger = <span className='ac-attribute-dropdown-item'><Icon iconName={"Like"} style={{color:'blue'}}/></span>
                break;
            case 'EXCLUDE':
                trigger = <span className='ac-attribute-dropdown-item'><Icon iconName={"StatusCircleBlock"} style={{color:'red'}}/></span>
                break;
            case 'MUST':
                trigger = <span className='ac-attribute-dropdown-item'><Icon iconName={"HeartFill"} style={{color:'red'}}/></span>;
                break;
        }
        return trigger;
    }
    
    const caretDown = () => {
        return <></>
    }

    const onRenderCell = (item?: IAttribute): JSX.Element => {
        if(item) {
            const currentOption = getFilterValue(item.value, item.name);
            // Selection control leads the row so the icons line up in a column, rather than sitting
            // ragged against the right of values of differing length.
            return (<div className={classNames.itemCell} data-is-focusable={true}>
                <div className={classNames.control}>
                    <Dropdown
                        styles={contentStyles}
                        selectedKey={currentOption}
                        id={item?.value}
                        className='dropdown'
                        ariaLabel={`Criteria for ${item.value}`}
                        options={options}
                        onRenderTitle={() => optionElement(currentOption)}
                        onRenderCaretDown={() => caretDown()}
                        onRenderOption={onRenderOption}
                        onChange={(a, b) => handleChange(a, b, item.value)}
                    />
                </div>
                <div className={classNames.itemContent}>
                    <div className={classNames.itemName} style={{fontSize: 12}} title={item?.value}>{item?.value}</div>
                </div>
            </div>);
        }
        return <></>;

    };
    return <>
        <FocusZone direction={FocusZoneDirection.vertical}>
            <List items={items ?? []} onRenderCell={onRenderCell} >
                
            </List>
        </FocusZone>
    </>;
};
